import { drawCellRectangles } from 'components/canvas/utils';
import { Dimensions } from 'constants/types';
import { useMemo } from 'react';
import { useSearchResult, useWellDropsWithIngredients } from 'redux/hooks/searchnFilter';
import { getPlateStructure } from 'utils/ExperimentUtils';
import { useCanvasWidthPercentage } from 'redux/hooks/common';
import { useWindowSize } from 'CustomHooks';
import { GetUnifiedExperimentCanvasWidth } from 'utils/Global';
import { TotalColumns } from 'components/canvas/constants';

export const useGenerateVirtualPlateWellsAndDetails = () => {
  const wellDrops = useWellDropsWithIngredients();
  const searchResult = useSearchResult();
  const canvasRectangleSize = useCanvasRectangleSize();
  const vPlateWells = useMemo(() => {
    if (!wellDrops.length || searchResult.rows.pagination.total < 1) {
      return {};
    }
    const wellDimensions: Dimensions = {
      width: canvasRectangleSize,
      height: canvasRectangleSize,
    };
    const containerRows = Math.ceil(searchResult.rows.items.length / TotalColumns);
    const containerCols = TotalColumns;
    const [plateHeight, plateWidth] = [
      wellDimensions.height * containerRows,
      wellDimensions.width * TotalColumns,
    ];

    const vPlateStructure = getPlateStructure(
      [{ colPos: 1, rowPos: 1 }],
      plateWidth,
      plateHeight,
      containerRows,
      containerCols,
      wellDimensions,
    )[0];
    let wellRectangles = drawCellRectangles(
      containerRows,
      containerCols,
      vPlateStructure.wellRects,
    );
    const differenceBetweenResultsAndRects =
      wellRectangles.length - searchResult.rows.items.length;
    wellRectangles =
      differenceBetweenResultsAndRects > 0
        ? wellRectangles.slice(0, -differenceBetweenResultsAndRects)
        : wellRectangles;

    return {
      wellRectangles,
      containerRows,
      containerCols,
      plateHeight,
      plateWidth,
      wellRects: vPlateStructure.wellRects,
      wellRectsPadded: vPlateStructure.wellRectsPadded,
      wellWithIngredients: wellDrops,
    };
  }, [wellDrops, searchResult, canvasRectangleSize]);

  return vPlateWells;
};
/**
 *
 * @returns Dynamic rectangle size based on screen size instead of one hardcoded rectangle size.
 * it will fill up the width. otherwise if we use hardcode rectangle size it will not cater all screens.
 */
export const useCanvasRectangleSize = (): number => {
  // its the percentage for one rectangle for available canvas width
  const RatioOfOneRectangleToCanvasWidth = 0.08;
  const windowSize = useWindowSize();
  const canvasWidthPercentage = useCanvasWidthPercentage();
  return useMemo(() => {
    const availableCanvasWidth = GetUnifiedExperimentCanvasWidth(canvasWidthPercentage);
    return Math.ceil(availableCanvasWidth * RatioOfOneRectangleToCanvasWidth);
  }, [windowSize, canvasWidthPercentage]);
};
